import React, { useState, useRef, useContext, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  CameraAltOutlined as CameraIcon,
  LogoutOutlined as LogoutIcon,
  DarkModeOutlined as DarkModeIcon,
  LightModeOutlined as LightModeIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  BadgeOutlined as BadgeIcon,
  BusinessOutlined as BusinessIcon,
  SwapHoriz as SwapIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  DoNotDisturb as DoNotDisturbIcon,
  UploadFile as UploadIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole, User } from 'shared/types';
import { userApi } from 'shared/services/userApi';
import { ColorModeContext } from 'shared/components/layout/ThemeContext';
import { userVerificationsApi, UserCompanyVerifications, VerificationDetail } from 'shared/services/userVerificationsApi';
import { VerificationSubmitDialog } from 'shared/components/verifications/VerificationSubmitDialog';
import { useQuery } from '@tanstack/react-query';
import { userPendingApi, PendingPerCompany } from 'shared/services/userPendingApi';
import { MeetingRoom as DoorIcon } from '@mui/icons-material';
import { getDefaultRouteForRole } from 'shared/utils/roleRedirects';
import { getHomeProviderAdminCompany } from 'shared/utils/roleUtils';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 2 * 1024 * 1024; // 2MB

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const getRoleLabel = (role: string | undefined): string => {
  if (!role) return 'Usuario';
  const roleLabels: Record<string, string> = {
    'super_admin': 'Super Admin',
    'safety_staff': 'Soporte Monitor',
    'company_admin': 'Administrador de Empresa',
    'client_approver': 'Aprobador',
    'client_staff': 'Colaborador',
    'security': 'Seguridad',
  };
  return roleLabels[role.toLowerCase()] || role;
};

const getStatusIcon = (status: string, size: number = 16) => {
  switch (status) {
    case 'approved':
      return <CheckCircleIcon sx={{ fontSize: size }} color="success" />;
    case 'rejected':
      return <ErrorIcon sx={{ fontSize: size }} color="error" />;
    case 'pending':
    case 'in_review':
      return <AccessTimeIcon sx={{ fontSize: size }} color="warning" />;
    case 'expired':
      return <WarningIcon sx={{ fontSize: size }} color="error" />;
    default:
      return <DoNotDisturbIcon sx={{ fontSize: size }} color="disabled" />;
  }
};

const getStatusLabel = (status: string) => {
  const statusMap: Record<string, string> = {
    'not_submitted': 'No enviado',
    'pending': 'Pendiente',
    'in_review': 'En revisión',
    'approved': 'Aprobado',
    'rejected': 'Rechazado',
    'expired': 'Expirado',
  };
  return statusMap[status] || status;
};

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'approved': return 'success';
    case 'rejected': return 'error';
    case 'expired': return 'error';
    case 'pending':
    case 'in_review': return 'warning';
    default: return 'default';
  }
};

const getComplianceColor = (status: string): 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'compliant': return 'success';
    case 'partial': return 'warning';
    default: return 'error';
  }
};

const getComplianceLabel = (status: string) => {
  switch (status) {
    case 'compliant': return 'Cumple';
    case 'partial': return 'Parcial';
    default: return 'No Cumple';
  }
};

export const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, userAvatarUrl, refreshUserAvatar, logout, switchCompany } = useAuth();

  // This user's own provider company, if they're its (active, non-external)
  // company_admin — regardless of which company is currently active. Only
  // relevant when they're NOT currently showing as company_admin already
  // (i.e. they're viewing themselves lent out as client_staff somewhere
  // else): "Colaborador" alone would hide that they actually run a
  // provider company.
  const homeProviderAdmin =
    user?.role !== UserRole.COMPANY_ADMIN ? getHomeProviderAdminCompany(user) : null;
  const { mode, toggleColorMode } = useContext(ColorModeContext);

  const [activeTab, setActiveTab] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Confirmation modal for company switching. User explicitly asked for
  // a confirmation step so a misclick doesn't silently re-scope all
  // their data to a different tenant.
  const [switchTarget, setSwitchTarget] = useState<{ id: string; name: string } | null>(null);
  const [switching, setSwitching] = useState(false);

  // Verification state
  const [verifications, setVerifications] = useState<UserCompanyVerifications[]>([]);
  const [verificationsLoaded, setVerificationsLoaded] = useState(false);
  const [verificationsLoading, setVerificationsLoading] = useState(false);
  const [verificationsError, setVerificationsError] = useState<string | null>(null);
  const [selectedVerification, setSelectedVerification] = useState<{
    verification: VerificationDetail;
    companyId: string;
    companyName: string;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadVerifications = useCallback(async () => {
    try {
      setVerificationsLoading(true);
      setVerificationsError(null);
      const data = await userVerificationsApi.getMyVerifications();
      setVerifications(data);
    } catch {
      setVerificationsError('Error al cargar verificaciones');
    } finally {
      setVerificationsLoading(false);
      setVerificationsLoaded(true);
    }
  }, []);

  // Load verifications when switching to the tab (only once)
  useEffect(() => {
    if (open && activeTab === 1 && !verificationsLoaded && !verificationsLoading) {
      loadVerifications();
    }
  }, [open, activeTab, verificationsLoaded, verificationsLoading, loadVerifications]);

  const initials = (user?.name || '??')
    .split(' ')
    .map((w: string) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Tipo de archivo no permitido. Use JPEG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('El archivo es muy grande. Máximo 2MB.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadUrlResponse = await userApi.getProfilePictureUploadUrl(file.type);
      if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
        throw new Error(uploadUrlResponse.message || 'Error al obtener URL de carga');
      }

      const { presignedPost, key } = uploadUrlResponse.data;

      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([field, value]) => {
        formData.append(field, value as string);
      });
      formData.append('file', file);

      const s3Response = await fetch(presignedPost.url, {
        method: 'POST',
        body: formData,
      });

      if (!s3Response.ok) {
        throw new Error('Error al subir archivo a S3');
      }

      const confirmResponse = await userApi.confirmProfilePictureUpload(key);
      if (!confirmResponse.success) {
        throw new Error(confirmResponse.message || 'Error al confirmar carga');
      }

      await refreshUserAvatar();
    } catch (err: any) {
      setError(err.message || 'Error al subir la foto');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/login');
  };

  const handleVerificationClick = (verification: VerificationDetail, companyId: string, companyName: string) => {
    setSelectedVerification({ verification, companyId, companyName });
    setDialogOpen(true);
  };

  if (!user) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 380,
          // Dark mode: match the main sidebar so the right drawer balances
          // the left rail. Light mode stays on `paper` (white) — using the
          // sidebar color there would render the drawer as dark navy.
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.background.sidebar
              : theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header with avatar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 2,
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          aria-label="Subir archivo"
        />
        <Box
          onClick={handleAvatarClick}
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            borderRadius: '50%',
            cursor: 'pointer',
            overflow: 'hidden',
            flexShrink: 0,
            '&:hover .camera-overlay': { opacity: 1 },
          }}
        >
          {userAvatarUrl ? (
            <Box
              component="img"
              src={userAvatarUrl}
              alt={user.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#fff',
            }}>
              {initials}
            </Box>
          )}
          <Box
            className="camera-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s ease',
            }}
          >
            {isUploading ? (
              <CircularProgress size={18} sx={{ color: 'background.buttonText' }} />
            ) : (
              <CameraIcon sx={{ color: '#fff', fontSize: 18 }} />
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" noWrap sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {user.name || '??'}
          </Typography>
          <Chip size="small" label={homeProviderAdmin ? 'Admin Proveedor' : getRoleLabel(user.role)} variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2.5, mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs — only client_staff (Colaboradores) carry per-company
          verifications. Hide for every other role so the drawer stays
          single-tab. The BE /user-verifications/me also returns an
          empty list for these roles as a second defense. */}
      {(() => {
        const showVerifications = user.role === UserRole.CLIENT_STAFF;
        if (!showVerifications) {
          // No Verificaciones tab for this role. Content below derives the
          // effective tab (always 0 here), so a stale `activeTab=1` never
          // renders an empty view — no state mutation during render.
          return <Divider />;
        }
        return (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                minHeight: 40,
                px: 2.5,
                '& .MuiTab-root': {
                  minHeight: 40,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                },
              }}
            >
              <Tab label="General" />
              <Tab label="Verificaciones" />
            </Tabs>
            <Divider />
          </>
        );
      })()}

      {/* Tab content — derive the effective tab so non-client_staff always
          see General regardless of a stale persisted `activeTab`. */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {(user.role === UserRole.CLIENT_STAFF ? activeTab : 0) === 0 && (
          <GeneralTab
            user={user}
            mode={mode}
            toggleColorMode={toggleColorMode}
            onRequestSwitchCompany={(id, name) => setSwitchTarget({ id, name })}
            drawerOpen={open}
            onClose={onClose}
          />
        )}
        {user.role === UserRole.CLIENT_STAFF && activeTab === 1 && (
          <VerificationsTab
            verifications={verifications}
            loading={verificationsLoading}
            error={verificationsError}
            onRetry={loadVerifications}
            onVerificationClick={handleVerificationClick}
          />
        )}
      </Box>

      {/* Logout button — always visible */}
      <Divider />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            fontWeight: 600,
            py: 1,
            color: 'background.buttonText',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Cerrar sesión
        </Button>
      </Box>

      {/* Verification submit dialog */}
      {selectedVerification && (
        <VerificationSubmitDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setSelectedVerification(null); }}
          verification={selectedVerification.verification}
          companyId={selectedVerification.companyId}
          companyName={selectedVerification.companyName}
          onSubmitted={loadVerifications}
          onShowMessage={() => {}}
        />
      )}

      {/* Company-switch confirmation. Required by design — silent
          switching was deemed confusing because the URL stays on a
          screen scoped to the old company. */}
      <Dialog
        open={!!switchTarget}
        onClose={() => !switching && setSwitchTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cambiar de empresa</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Está cambiando a <strong>{switchTarget?.name}</strong>. ¿Continuar?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Todas las pantallas se recargarán mostrando la información de la
            empresa seleccionada. Su sesión y permisos se mantienen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSwitchTarget(null)} disabled={switching}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!switchTarget) return;
              setSwitching(true);
              try {
                await switchCompany(switchTarget.id);
                setSwitchTarget(null);
                onClose();
                // Send the user to a screen that exists for their (now
                // possibly different) effective role. Going to a path
                // they can't access would be a worse experience than a
                // tiny detour through the landing page.
                navigate('/');
              } catch (err: any) {
                setError(err?.message || 'No se pudo cambiar de empresa.');
              } finally {
                setSwitching(false);
              }
            }}
            startIcon={switching ? <CircularProgress size={16} sx={{ color: 'background.buttonText' }} /> : <SwapIcon fontSize="small" />}
            disabled={switching}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

// ─── General Tab ─────────────────────────────────────────────
const GeneralTab: React.FC<{
  user: User;
  mode: string;
  toggleColorMode: () => void;
  onRequestSwitchCompany: (targetCompanyId: string, targetCompanyName: string) => void;
  drawerOpen: boolean;
  onClose: () => void;
}> = ({ user, mode, toggleColorMode, onRequestSwitchCompany, drawerOpen, onClose }) => {
  // Resolve every cliente/proveedor entry the user belongs to. Pulled
  // off the populated `companies[]` array on the user blob — entries
  // can carry `company` as a populated object or a bare ObjectId
  // string; we only surface ones with a populated object (otherwise
  // there's nothing meaningful to show or switch to).
  const companyEntries = (user?.companies || [])
    .filter((c: any) => c?.company && typeof c.company === 'object' && c.isActive !== false)
    .map((c: any) => ({
      id: String(c.company._id || c.company.id),
      name: c.company.name as string,
      role: c.role as string | undefined,
      isExternal: !!c.isExternal,
      // Only ever false for the own (non-external) membership of a
      // proveedor company with no active plan — see getOwnPlanlessProviderBlock.
      planBlocked: c.hasOwnActivePlan === false,
    }));
  const activeCompanyId = String((user?.company?._id || user?.companyId || '') as string);
  const hasMultiple = companyEntries.length > 1;

  // Same "Colaborador" → "Admin Proveedor" relabeling as the header Chip
  // above (ProfileDrawer itself) — recomputed here because GeneralTab only
  // receives `user` as a prop, not via useAuth().
  const homeProviderAdmin =
    user?.role !== UserRole.COMPANY_ADMIN ? getHomeProviderAdminCompany(user) : null;

  // Platform-role switcher (super_admin <-> safety_staff). Only identities
  // holding more than one platform role can switch.
  const navigate = useNavigate();
  const { switchGlobalRole } = useAuth();
  const globalRoles = (user?.globalRoles || []) as string[];
  const activeGlobalRole = (user?.globalRole || user?.role) as string | undefined;
  const canSwitchRole = globalRoles.length > 1;
  const [switchingRole, setSwitchingRole] = React.useState<string | null>(null);
  const [roleSwitchError, setRoleSwitchError] = React.useState<string | null>(null);

  const handleSwitchRole = async (role: string) => {
    if (role === activeGlobalRole || switchingRole) return;
    setSwitchingRole(role);
    setRoleSwitchError(null);
    try {
      await switchGlobalRole(role);
      // Land on the same default screen a fresh login under this role
      // would — the old page (and its now-stale data scope) isn't
      // necessarily meaningful under the new role.
      onClose();
      navigate(getDefaultRouteForRole(role as UserRole));
    } catch (e: unknown) {
      setRoleSwitchError(e instanceof Error ? e.message : 'No se pudo cambiar de rol');
    } finally {
      setSwitchingRole(null);
    }
  };

  // Fetch pending counts only when the drawer is open AND the user has
  // more than one company (the section that consumes the data is only
  // rendered in that case). React Query dedupes across re-opens.
  const { data: pendingList } = useQuery<PendingPerCompany[]>({
    queryKey: ['userPendingAcrossCompanies', user?._id || user?.id],
    queryFn: () => userPendingApi.getAcrossCompanies(),
    enabled: drawerOpen && hasMultiple,
    staleTime: 60 * 1000,
  });
  const pendingByCompany = React.useMemo(() => {
    const map = new Map<string, PendingPerCompany['pending']>();
    (pendingList || []).forEach((p) => map.set(p.companyId, p.pending));
    return map;
  }, [pendingList]);
  const logoByCompany = React.useMemo(() => {
    const map = new Map<string, string | null | undefined>();
    (pendingList || []).forEach((p) => map.set(p.companyId, p.logoUrl ?? null));
    return map;
  }, [pendingList]);

  return (
    <Box>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <InfoRow icon={<EmailIcon />} label="Email" value={user.email} />
        {user.phone && <InfoRow icon={<PhoneIcon />} label="Teléfono" value={user.phone} />}
        <InfoRow icon={<BadgeIcon />} label="Rol" value={homeProviderAdmin ? 'Admin Proveedor' : getRoleLabel(user.role)} />
        {user.company?.name && (
          <InfoRow icon={<BusinessIcon />} label="Empresa activa" value={user.company.name} />
        )}
      </Box>

      {/* Company switcher — only surfaced for multi-company users. */}
      {hasMultiple && (
        <>
          <Divider />
          <Box sx={{ px: 2.5, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SwapIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Cambiar de empresa
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Al cambiar de empresa, todas las pantallas (usuarios, verificaciones,
              marketplace, etc.) mostrarán la información de la empresa seleccionada.
              Su sesión y permisos se mantienen.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {companyEntries.map((c: { id: string; name: string; role?: string; isExternal: boolean; planBlocked: boolean }) => {
                const isActive = c.id === activeCompanyId;
                // Skip the chip on the active company — the user can already
                // see those pendings in-app. We only want the at-a-glance
                // signal for the OTHER companies the user could switch to.
                const pending = !isActive ? pendingByCompany.get(c.id) : undefined;
                const hasPending = !!pending && pending.total > 0;
                const logoUrl = logoByCompany.get(c.id);
                const pendingBreakdown = pending
                  ? [
                      pending.verifications > 0
                        ? `${pending.verifications} ${pending.verifications === 1 ? 'verificación' : 'verificaciones'}`
                        : null,
                      pending.evaluations > 0
                        ? `${pending.evaluations} ${pending.evaluations === 1 ? 'evaluación' : 'evaluaciones'}`
                        : null,
                      pending.permits > 0
                        ? `${pending.permits} ${pending.permits === 1 ? 'permiso' : 'permisos'}`
                        : null,
                      pending.serviceCards > 0
                        ? `${pending.serviceCards} ${pending.serviceCards === 1 ? 'ficha' : 'fichas'}`
                        : null,
                    ].filter(Boolean).join(', ')
                  : '';
                return (
                  <Box
                    key={c.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1,
                      py: 1,
                      borderRadius: 1.5,
                      ...(isActive && { bgcolor: 'action.selected' }),
                    }}
                  >
                    {logoUrl ? (
                      <Avatar src={logoUrl} alt={c.name} variant="rounded" sx={{ width: 36, height: 36 }} />
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={isActive ? 700 : 600} noWrap>
                        {c.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                        {c.role && (
                          <Typography variant="caption" color="text.secondary">
                            {c.isExternal && homeProviderAdmin ? 'Admin Proveedor' : getRoleLabel(c.role)}
                          </Typography>
                        )}
                        {c.isExternal && (
                          <Chip
                            label="Externo"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: 'action.hover',
                              color: 'text.secondary',
                              border: 'none',
                            }}
                          />
                        )}
                        {c.planBlocked && (
                          <Tooltip title="Contacte a su Soporte Monitor para adquirir un plan" arrow>
                            <Chip
                              label="Sin plan activo"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                border: 'none',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    {hasPending && (
                      <Tooltip
                        title={
                          pendingBreakdown
                            ? `Pendientes en esta empresa: ${pendingBreakdown}.`
                            : 'Hay elementos pendientes en esta empresa.'
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 26,
                            height: 22,
                            px: 0.9,
                            borderRadius: 999,
                            bgcolor: 'warning.main',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.74rem',
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          {pending!.total}
                        </Box>
                      </Tooltip>
                    )}
                    {isActive ? (
                      <Chip
                        label="Activa"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: (t) => `${t.palette.primary.main}1A`,
                          color: 'primary.main',
                          border: 'none',
                        }}
                      />
                    ) : c.planBlocked ? (
                      <Tooltip title="Sin plan propio — no se puede ingresar a esta empresa" arrow>
                        <span>
                          <IconButton size="small" disabled sx={{ flexShrink: 0 }}>
                            <LockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Entrar a esta empresa" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onRequestSwitchCompany(c.id, c.name)}
                          aria-label={`Entrar a ${c.name}`}
                          sx={{ flexShrink: 0 }}
                        >
                          <DoorIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}

      {/* Platform-role switcher — only for identities holding more than one
          platform role (super_admin + safety_staff). Switching re-issues the
          JWT and reloads data under the chosen role. */}
      {canSwitchRole && (
        <>
          <Divider />
          <Box sx={{ px: 2.5, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SwapIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Cambiar de rol
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Su cuenta tiene más de un rol de plataforma. Al cambiar de rol, las
              pantallas se recargan con los datos y permisos de ese rol. Su sesión
              se mantiene.
            </Typography>
            {roleSwitchError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setRoleSwitchError(null)}>
                {roleSwitchError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {globalRoles.map((r) => {
                const isActive = r === activeGlobalRole;
                const isSwitching = switchingRole === r;
                return (
                  <Box
                    key={r}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1,
                      py: 1,
                      borderRadius: 1.5,
                      ...(isActive && { bgcolor: 'action.selected' }),
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.secondary' }}
                    >
                      <BadgeIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={isActive ? 700 : 600} noWrap>
                        {getRoleLabel(r)}
                      </Typography>
                    </Box>
                    {isActive ? (
                      <Chip
                        label="Activo"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: (t) => `${t.palette.primary.main}1A`,
                          color: 'primary.main',
                          border: 'none',
                        }}
                      />
                    ) : (
                      <Tooltip title={`Actuar como ${getRoleLabel(r)}`} arrow>
                        <span style={{ display: 'inline-flex' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSwitchRole(r)}
                            disabled={!!switchingRole}
                            aria-label={`Cambiar a ${getRoleLabel(r)}`}
                            sx={{ flexShrink: 0 }}
                          >
                            {isSwitching ? <CircularProgress size={18} /> : <DoorIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}

      <Divider />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Box
          onClick={toggleColorMode}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            transition: 'background-color 0.2s ease',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {mode === 'dark' ? (
            <LightModeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          ) : (
            <DarkModeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          )}
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            {mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Verifications Tab ───────────────────────────────────────
const VerificationsTab: React.FC<{
  verifications: UserCompanyVerifications[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onVerificationClick: (v: VerificationDetail, companyId: string, companyName: string) => void;
}> = ({ verifications, loading, error, onRetry, onVerificationClick }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={onRetry}>Reintentar</Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (verifications.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 2.5 }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No hay verificaciones asignadas
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {verifications.map((companyData) => {
        const required = companyData.verifications.required;
        const completed = companyData.verifications.completed;
        const progress = required > 0 ? Math.round((completed / required) * 100) : 100;
        // If there are no requirements, treat as compliant regardless of API status
        const effectiveStatus = required === 0 ? 'compliant' : companyData.complianceStatus;

        return (
          <Box key={companyData.company.id} sx={{ mb: 1 }}>
            {/* Company header */}
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {companyData.company.name}
                </Typography>
                <Chip
                  label={required === 0 ? 'Sin requisitos' : getComplianceLabel(effectiveStatus)}
                  color={getComplianceColor(effectiveStatus)}
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  color={getComplianceColor(effectiveStatus)}
                  sx={{ flex: 1, height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                  {companyData.verifications.completed}/{companyData.verifications.required}
                </Typography>
              </Box>
            </Box>

            {/* Verification items */}
            {companyData.verifications.details.map((v) => (
              <Box
                key={v.id}
                onClick={() => onVerificationClick(v, companyData.company.id, companyData.company.name)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2.5,
                  py: 1,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {getStatusIcon(v.status)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                    {v.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {v.type}{v.category ? ` · ${v.category}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {v.isRequired && (
                    <Chip label="Req" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
                  )}
                  <Chip
                    label={getStatusLabel(v.status)}
                    size="small"
                    color={getStatusColor(v.status)}
                    sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                  {v.status === 'not_submitted' && (
                    <UploadIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Info Row ────────────────────────────────────────────────
const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 18 } }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);
